const { SORT_CATEGORIES } = require('./constants');

/**
 * aとbで作成日時を比較し、aの方が古ければ正の数、bの方が古ければ負の数を返す
 *
 * @param {object} a
 * @param {object} b
 * @return {integer} 比較結果
 */
const compCreatedAt = (a, b) => {
  if (a.createdAt < b.createdAt) return 1;
  if (a.createdAt > b.createdAt) return -1;
  return 0;
};

/**
 * bの受講者セルフチェックの正答数からaの受講者セルフチェックの正答数を減算した数を返す
 * aがセルフチェックをしていない場合正の数、bがセルフチェックをしていない場合負の数を返す
 *
 * @param {object} a
 * @param {object} b
 * @return {integer} 比較結果
 */
const compCorrectAnswerCount = (a, b) => {
  if (a.selfCheckResult === null) return 1;
  if (b.selfCheckResult === null) return -1;
  return b.selfCheckResult.correctAnswerCount - a.selfCheckResult.correctAnswerCount;
};

/**
 * aの社員番号からbの社員番号を減算した数を返す
 *
 * @param {object} a
 * @param {object} b
 * @return {integer} 比較結果
 */
const compEmployeeNumber = (a, b) => {
  if (a.driver && b.driver) {
    return Number(a.driver.employeeNumber) - Number(b.driver.employeeNumber);
  } else {
    return Number(a.employeeNumber) - Number(b.employeeNumber);
  }
};

/**
 * aとbで社員指名を比較し、aの方が50音順で大きければ正の数、bの方が50音順で大きければ負の数を返す
 *
 * @param {object} a
 * @param {object} b
 * @return {integer} 比較結果
 */
const compEmployeeName = (a, b) => {
  const nameA = a.name || a.driver.name;
  const nameB = b.name || b.driver.name;
  return nameA.localeCompare(nameB, 'ja');
};

/**
 * aの線区コードからbの線区コードを減算した数を返す
 *
 * @param {object} a
 * @param {object} b
 * @return {integer} 比較結果
 */
const compSection = (a, b) => {
  return Number(a.section.code) - Number(b.section.code);
};

/**
 * aとbで通告カテゴリーコードを比較し、aの方が大きければ正の数、bの方が大きければ負の数を返す
 *
 * @param {object} a
 * @param {object} b
 * @return {integer} 比較結果
 */
const compNoticeCategory = (a, b) => {
  if (a.noticeCategoryCode > b.noticeCategoryCode) return 1;
  if (a.noticeCategoryCode < b.noticeCategoryCode) return -1;
  return 0;
};

/**
 * aとbで受講開始日時を比較し、aの方が古ければ正の数、bの方が古ければ負の数を返す
 *
 * @param {object} a
 * @param {object} b
 * @return {integer} 比較結果
 */
const compStartedAt = (a, b) => {
  if (a.startedAt < b.startedAt) return 1;
  if (a.startedAt > b.startedAt) return -1;
  return 0;
};

/**
 * aとbでECを比較し、aの方が新しければ正の数、bの方が新しければ負の数を返す
 *
 * @param {object} a
 * @param {object} b
 * @param {boolean} isDesc
 * @return {integer} 比較結果
 */
const compEC = (a, b, isDesc) => {
  const regex = /(新|旧)?(\d+)/;
  const matchesA = a.className.match(regex);
  const matchesB = b.className.match(regex);

  // ECなしの場合は最後に表示する
  if (!matchesA) {
    return isDesc ? -1 : 1;
  }
  if (!matchesB) {
    return isDesc ? 1 : -1;
  }

  let [, prefixA, numA] = matchesA;
  let [, prefixB, numB] = matchesB;

  if (prefixA === undefined) prefixA = '新';
  if (prefixB === undefined) prefixB = '新';

  const intA = parseInt(numA, 10);
  const intB = parseInt(numB, 10);

  // Compare based on prefix.
  switch (prefixA) {
    case '旧':
      if (prefixB === '新') {
        return -1;
      }
      break;
    case '新':
      if (prefixB === '旧') {
        return 1;
      }
      break;
  }

  // Compare based on number.
  if (intA < intB) {
    return -1;
  }
  if (intA > intB) {
    return 1;
  }
  return 0;
};

/**
 * 入力された配列を並び替える
 *
 * @param {array} arr
 * @param {string} sortMethod
 * @return {array} 並び替え後の配列
 */
export const sortTableData = (arr, sortMethod) => {
  arr = structuredClone(arr);

  switch (sortMethod) {
    case SORT_CATEGORIES.CREATED_DESC:
      arr.sort((a, b) => {
        return compCreatedAt(a, b);
      });
      break;
    case SORT_CATEGORIES.CREATED_ASC:
      arr.sort((a, b) => {
        return compCreatedAt(b, a);
      });
      break;
    case SORT_CATEGORIES.EMPLOYEE_NUMBER_ASC:
      arr.sort((a, b) => {
        return compEmployeeNumber(a, b);
      });
      break;
    case SORT_CATEGORIES.EMPLOYEE_NAME_ASC:
      arr.sort((a, b) => {
        return compEmployeeName(a, b);
      });
      break;
    case SORT_CATEGORIES.CORRECT_ANSWER_DESC:
      arr.sort((a, b) => {
        return compCorrectAnswerCount(a, b);
      });
      break;
    case SORT_CATEGORIES.CORRECT_ANSWER_ASC:
      arr.sort((a, b) => {
        return compCorrectAnswerCount(b, a);
      });
      break;
    case SORT_CATEGORIES.SECTION_NUMBER_ASC:
      arr.sort((a, b) => {
        return compSection(a, b);
      });
      break;
    case SORT_CATEGORIES.SECTION_NUMBER_DESC:
      arr.sort((a, b) => {
        return compSection(b, a);
      });
      break;
    case SORT_CATEGORIES.NOTICE_CATEGORIES_ASC:
      arr.sort((a, b) => {
        return compNoticeCategory(a, b);
      });
      break;
    case SORT_CATEGORIES.NOTICE_CATEGORIES_DESC:
      arr.sort((a, b) => {
        return compNoticeCategory(b, a);
      });
      break;
    case SORT_CATEGORIES.TRAINED_DESC:
      arr.sort((a, b) => {
        return compStartedAt(a, b);
      });
      break;
    case SORT_CATEGORIES.TRAINED_ASC:
      arr.sort((a, b) => {
        return compStartedAt(b, a);
      });
      break;
    case SORT_CATEGORIES.EC_DESC:
      arr.sort((a, b) => {
        return compEC(b, a, true);
      });
      break;
    case SORT_CATEGORIES.EC_ASC:
      arr.sort((a, b) => {
        return compEC(a, b, false);
      });
      break;
  };
  return arr;
};
